import { createSelector } from 'reselect';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IEntityReducer } from 'core/reducers/entities';
import { IActionBasic } from 'types/domain/action';
import { parseAction } from './action';
import { IActionFeed, IActionFeedItem, selectActionFeed } from './actionFeed';

const entity = (state: RootState) => state.entity.present;
const id = (state: RootState, id?: number) => id || state.state[EntityTypes.ACTION];
const feed = selectActionFeed;

const getActionConfigStart = (action: IActionBasic) => {
  const config = action?.voiceSceneConfigId ? action.voiceSceneConfig : action.sceneConfig;
  return config?.start;
};

const getActionSceneId = (action: IActionBasic) => action?.voiceSceneId ?? action?.sceneId;

/**
 * An action's effective `end` on `sceneId` — its own config's end, falling back to
 * the scene's (the same per-key merge selectSceneConfig does). `sceneId` may be the
 * action's scene or its voiceover scene. `undefined` when the action isn't on that
 * scene; `null` when it plays to the end of the video.
 */
function getMergedSceneEnd(entity: IEntityReducer, actionId: number, sceneId: number): number | null | undefined {
  const action = actionId ? entity[EntityTypes.ACTION][actionId] : null;
  if (!action) return undefined;

  const ownConfigId =
    action.voiceSceneId === sceneId
      ? action.voiceSceneConfigId
      : action.sceneId === sceneId
      ? action.sceneConfigId
      : undefined;
  if (ownConfigId === undefined) return undefined;

  const scene = entity[EntityTypes.SCENE][sceneId];
  const own = ownConfigId != null ? entity[EntityTypes.SCENE_CONFIG][ownConfigId] : null;
  const base = scene ? entity[EntityTypes.SCENE_CONFIG][scene.sceneConfigId] : null;

  return (own?.end ?? base?.end) || null;
}

export const selectActionSceneEnd = (state: RootState, actionId: number, sceneId: number) =>
  getMergedSceneEnd(state.entity.present, actionId, sceneId);

/**
 * The furthest `end` of any action on `sceneId` in the module of the current action
 * (or its neighbour, while a scene loads ahead of or behind the action pointer). One
 * loaded source serves every action on the scene — moving between them only seeks —
 * so its `#t=…,end` must cover all of them, not just the one current when it loaded.
 *
 * `undefined` when no action near the current one is on the scene; `null` when any
 * of them plays to the end of the video.
 */
export function selectSceneRunEnd(state: RootState, sceneId: number): number | null | undefined {
  const entity = state.entity.present;
  const { feed, index } = selectActionFeed(state);
  const current = feed.findIndex(item => item.actionId === state.state[EntityTypes.ACTION]);
  const pivot = current === -1 ? index : current;
  const onScene = (i: number) => getMergedSceneEnd(entity, feed[i]?.actionId, sceneId) !== undefined;

  const near = [pivot, pivot - 1, pivot + 1].find(onScene);
  if (near === undefined) return undefined;

  // The feed is only what has been reached so far: a scenario's next slide is whichever
  // branch gets picked, so which actions follow on this video isn't known when it
  // loads. Cover every action in the module that plays it — a source cut short is
  // rebuilt when an action runs past it, and that reload is a black flash.
  const moduleId = entity[EntityTypes.ACTION][feed[near].actionId]?.moduleId;

  const run = Object.values(entity[EntityTypes.ACTION])
    .filter(action => action.status > -1 && action.moduleId === moduleId)
    .map(action => action.id)
    .filter(actionId => getMergedSceneEnd(entity, actionId, sceneId) !== undefined);

  let runEnd = 0;
  for (const actionId of run) {
    const end = getMergedSceneEnd(entity, actionId, sceneId);
    if (end == null) return null;
    runEnd = Math.max(runEnd, end);
  }

  return runEnd;
}

export const selectActionEndTime = createSelector(
  [entity, id, feed],
  (entity: IEntityReducer, id: number, feed: IActionFeed) => {
    const action = parseAction(entity, id);

    if (!action) return null;

    // Merged with the scene's end, as the source is cut: the source can run on past this
    // action (it covers every action on the video), so an end only inherited from the
    // scene still has to be caught here.
    const actionEnd = getMergedSceneEnd(entity, action.id, getActionSceneId(action));
    if (actionEnd == null) return null;

    const currentIndex = feed.feed.findIndex((item: IActionFeedItem) => item.actionId === id);
    if (currentIndex === -1) return actionEnd;

    const nextActionId = feed.feed[currentIndex + 1]?.actionId;
    if (!nextActionId) return actionEnd;

    const nextAction = parseAction(entity, nextActionId);
    if (!nextAction) return actionEnd;

    const hasSameScene = getActionSceneId(action) === getActionSceneId(nextAction);
    if (!hasSameScene) return actionEnd;

    const nextActionStart = getActionConfigStart(nextAction);

    return nextActionStart != null && nextActionStart < actionEnd ? nextActionStart : actionEnd;
  },
);
